import type { FeatureFlagT } from "../proctor/FeatureFlags";
import type JobTagStrictness from "../requirements/JobTagStrictness";
import type { QualificationAttribute } from "../types/JobAttribute";

import { translateMCEComparisonsToAttributes } from "./attributes.utils";
import Sentiment from "../../../../../../shared/util/salary/Sentiment";
import ViewType from "../constants/ViewType";
import {
  type default as TaxonomyEntity,
  InferenceMethod,
  Source,
  Status,
} from "../requirements/TaxonomyEntity";

export function getJobTagIdToStrictnessFromRequirements(jobTagRequirements: {
  [key: string]: { [key: string]: string };
}): { [key: string]: JobTagStrictness } {
  return Object.values(jobTagRequirements).reduce(
    (acc: { [key: string]: JobTagStrictness }, curr: { [key: string]: string }) => {
      if (curr.attributeId) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        acc[curr.attributeId] = curr.strictness as JobTagStrictness;
      }
      if (curr.attributeSuid) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        acc[curr.attributeSuid] = curr.strictness as JobTagStrictness;
      }
      return acc;
    },
    {},
  );
}

export function wasInferred(inferenceMethod?: InferenceMethod): boolean {
  return new Set([
    InferenceMethod.STRONGLY_INFERRED,
    InferenceMethod.WEAKLY_INFERRED,
    InferenceMethod.EXTRACTED,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  ]).has(inferenceMethod as any);
}

export function getAttributesAndViewType({
  mceModel,
  taxonomyIdToJssdId,
  featureFlags,
}: {
  mceModel: component.oneGraphMatchComparison.OneGraphMatchComparison;
  taxonomyIdToJssdId: { [key: string]: string };
  featureFlags: FeatureFlagT;
}): { viewType: ViewType; attributes: QualificationAttribute[] } {
  const viewType = getViewType(mceModel);
  const attributes = translateMCEComparisonsToAttributes(
    {
      attributeComparisons: mceModel?.attributeComparisons || [],
      taxonomyIdToJssdId,
    },
    featureFlags,
  );

  return {
    viewType,
    attributes: filterRequirementsByViewType({
      viewType,
      attributes,
    }),
  };
}

export function filterRequirementsByViewType({
  attributes,
  viewType,
}: {
  attributes: QualificationAttribute[];
  viewType: ViewType;
}): QualificationAttribute[] {
  switch (viewType) {
    case ViewType.YELLOW:
      return attributes;
    case ViewType.RED:
      const unmetRequirements = attributes.filter(
        (attr) => attr.sentiment === Sentiment.NEGATIVE || attr.sentiment === Sentiment.UNKNOWN,
      );
      return unmetRequirements;
    case ViewType.GREEN:
      const metRequirements = attributes.filter((attr) => attr.sentiment === Sentiment.POSITIVE);
      return metRequirements;
    case ViewType.UNKNOWN:
    default:
      return [];
  }
}

export type ShouldShowParams = {
  hasRequirements: boolean;
  loggedIn: boolean;
  featureFlags: FeatureFlagT;
  viewType: ViewType;
};

export const getIsMatchInsightsSectionEnabled = ({
  hasRequirements,
  loggedIn,
  viewType,
  featureFlags: { shouldHideProfileInsights },
}: ShouldShowParams): boolean => {
  const hasViewType = ViewType.UNKNOWN !== viewType;
  const hasPermission = loggedIn;
  const profileInsightsSectionEnabled = !(
    !hasPermission ||
    !hasRequirements ||
    !hasViewType ||
    shouldHideProfileInsights
  );

  return profileInsightsSectionEnabled;
};

export function filterTaxonomyEntities(taxonomyEntityList: TaxonomyEntity[]): TaxonomyEntity[] {
  const negativeMatchIds = new Set();
  taxonomyEntityList
    .filter((entity) => entity.status === Status.NEGATIVE_MATCH)
    .forEach((negMatch: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      negativeMatchIds.add(negMatch.id);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      negativeMatchIds.add(negMatch.suid);
    });

  const filteredEntities = taxonomyEntityList
    .filter(
      (entity) => !(negativeMatchIds.has(entity.id) && entity.status !== Status.NEGATIVE_MATCH),
    )
    .filter((entity) => entity.source === Source.JOB || entity.source === Source.BOTH);

  return filteredEntities;
}

export function getViewType(
  mceModel?: component.oneGraphMatchComparison.OneGraphMatchComparison,
): ViewType {
  switch (mceModel?.encouragementToApply?.trafficLight) {
    case "GREEN":
      return ViewType.GREEN;
    case "YELLOW":
      return ViewType.YELLOW;
    case "RED":
      return ViewType.RED;
    default:
      return ViewType.UNKNOWN;
  }
}

export function getTaxonomyIdToJssdId(
  jobSeekerSuidsToFactIds?: platform.model.JobSeekerSuidToFactId[],
) {
  const result: { [key: string]: string } = {};

  if (jobSeekerSuidsToFactIds) {
    jobSeekerSuidsToFactIds.forEach((idPair) => {
      result[idPair.suid] = idPair.factId;
    });
  }

  return result;
}
